import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  BarChart3,
  BedDouble,
  Building2,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Layers3,
  Menu,
  Receipt,
  ShieldCheck,
  Sparkles,
  Star,
  Users2,
  Wallet2,
  X
} from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const openRegister = (plan = 'Gold') => {
    navigate(`/login?mode=register&plan=${encodeURIComponent(plan)}`);
  };

  const features = [
    {
      icon: Users2,
      title: 'Tenant Management',
      description: 'Handle onboarding, move-ins, transfers, and vacates without chasing scattered records.'
    },
    {
      icon: Receipt,
      title: 'Rent Tracking',
      description: 'Track collections, dues, and payment history with cleaner monthly visibility.'
    },
    {
      icon: BedDouble,
      title: 'Visual Room Control',
      description: 'Manage floors, rooms, and beds from a visual layout built for daily PG operations.'
    },
    {
      icon: BarChart3,
      title: 'Business Reporting',
      description: 'Review occupancy, expenses, and profit trends in a more operationally useful way.'
    }
  ];

  const whyChooseUs = [
    {
      icon: Clock3,
      title: 'Faster daily workflows',
      description: 'Spend less time updating manual sheets and more time running the business.'
    },
    {
      icon: ShieldCheck,
      title: 'Cleaner operational accuracy',
      description: 'Keep occupancy, tenants, payments, and expenses aligned in one system.'
    },
    {
      icon: Layers3,
      title: 'Made for growing properties',
      description: 'Works for compact PGs and scales naturally as your room and floor structure grows.'
    }
  ];

  const pricing = [
    {
      name: 'Standard',
      price: 20,
      features: [
        'Tenant Management',
        'Rent Tracking',
        'Basic Reports',
        'Email Support'
      ],
      popular: false
    },
    {
      name: 'Gold',
      price: 25,
      features: [
        'Everything in Standard',
        'Expense Management',
        'Analytics Dashboard',
        'Priority Support'
      ],
      popular: true
    },
    {
      name: 'Platinum',
      price: 30,
      features: [
        'Everything in Gold',
        'Custom Reports',
        'Google Visibility',
        '24/7 Priority Support'
      ],
      popular: false
    }
  ];

  const faqs = [
    {
      question: 'What is Solucate built for?',
      answer: 'Solucate is designed for PG and hostel operators who want a cleaner system for occupancy, tenant workflows, rent collection, expenses, and monthly business visibility.'
    },
    {
      question: 'Will it work well on mobile too?',
      answer: 'Yes. The layout is responsive for phones, tablets, laptops, and larger screens so operational work stays comfortable everywhere.'
    },
    {
      question: 'Can I manage floors, rooms, and beds visually?',
      answer: 'Yes. Solucate includes floor, room, and bed workflows with visual layout support so operational changes are easier to understand.'
    },
    {
      question: 'Does it support monthwise reporting?',
      answer: 'Yes. Solucate helps you review monthly collections, logged expenses, and financial-year performance directly in the product.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#f6f7f2] text-slate-900">
      <div className="absolute inset-x-0 top-0 h-[560px] overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-dot-grid opacity-60" />
        <div className="absolute left-[-8%] top-[-6%] h-64 w-64 rounded-full bg-emerald-200/45 blur-3xl animate-pulse-soft" />
        <div className="absolute right-[4%] top-[6%] h-60 w-60 rounded-full bg-cyan-200/35 blur-3xl animate-pulse-soft animation-delay-300" />
      </div>

      <nav className="sticky top-0 z-50 border-b border-slate-200/80 bg-[#f6f7f2]/90 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3">
            <button onClick={() => navigate('/')} className="flex items-center gap-3" data-testid="brand-home">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-950 shadow-[0_12px_30px_rgba(15,23,42,0.16)]">
                <Sparkles className="h-5 w-5 text-emerald-300" />
              </div>
              <div className="text-left">
                <p className="text-lg font-bold tracking-tight text-slate-950">Solucate</p>
                <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">PG Operations OS</p>
              </div>
            </button>

            <div className="hidden md:flex items-center gap-7">
              <a href="#platform" className="text-sm font-medium text-slate-700 hover:text-slate-950 transition-colors">Platform</a>
              <a href="#pricing" className="text-sm font-medium text-slate-700 hover:text-slate-950 transition-colors">Pricing</a>
              <a href="#faq" className="text-sm font-medium text-slate-700 hover:text-slate-950 transition-colors">FAQ</a>
              <Button onClick={() => navigate('/login')} variant="ghost" className="rounded-full px-4 font-semibold text-slate-700">
                Login
              </Button>
              <Button
                onClick={() => openRegister('Gold')}
                className="rounded-full bg-slate-950 px-5 font-semibold text-white hover:bg-slate-800"
                data-testid="nav-register"
              >
                Start with Solucate
              </Button>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden rounded-xl border border-slate-200 bg-white p-2"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur-xl">
            <div className="px-4 py-4 space-y-3">
              <a href="#platform" className="block py-2 text-slate-700 font-medium">Platform</a>
              <a href="#pricing" className="block py-2 text-slate-700 font-medium">Pricing</a>
              <a href="#faq" className="block py-2 text-slate-700 font-medium">FAQ</a>
              <Button onClick={() => navigate('/login')} variant="outline" className="w-full rounded-xl">
                Login
              </Button>
              <Button onClick={() => openRegister('Gold')} className="w-full rounded-xl bg-slate-950 text-white hover:bg-slate-800">
                Start with Solucate
              </Button>
            </div>
          </div>
        )}
      </nav>

      <section className="relative pt-14 pb-16 sm:pt-16 sm:pb-20 lg:pt-24 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="animate-fade-up">
              <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-emerald-200 bg-white/85 px-4 py-2 text-sm font-medium text-emerald-800 shadow-sm">
                <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse-soft" />
                Professional software for modern PG operators
              </div>

              <h1 className="mt-6 max-w-3xl text-4xl font-bold leading-tight tracking-tight text-slate-950 sm:text-5xl lg:text-6xl">
                Run your PG with a calmer, sharper operating system called <span className="text-emerald-700">Solucate</span>.
              </h1>

              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 sm:text-xl">
                Solucate brings tenant operations, room management, rent tracking, and monthwise financial visibility into one product that feels more like modern SaaS and less like patched admin software.
              </p>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
                <Button
                  onClick={() => openRegister('Gold')}
                  className="h-14 w-full sm:w-auto rounded-full bg-slate-950 px-7 text-base font-semibold text-white shadow-[0_14px_40px_rgba(15,23,42,0.22)] transition-transform hover:-translate-y-0.5 hover:bg-slate-800"
                  data-testid="hero-register"
                >
                  Launch Solucate
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                  variant="outline"
                  className="h-14 w-full sm:w-auto rounded-full border-slate-300 bg-white/90 px-7 text-base font-semibold text-slate-800 hover:bg-white"
                >
                  View Pricing
                </Button>
              </div>

              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-600">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  Responsive across mobile, tablet, and desktop
                </div>
                <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-600">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  Built for monthwise business clarity
                </div>
              </div>
            </div>

            <div className="animate-fade-up animation-delay-150">
              <div className="glass-panel overflow-hidden rounded-[30px] border border-white/70 p-3 sm:p-4 shadow-[0_30px_80px_rgba(15,23,42,0.12)]">
                <div className="rounded-[24px] border border-slate-200 bg-white p-4 sm:p-5">
                  <div className="flex flex-col gap-4 border-b border-slate-100 pb-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700">Solucate Experience</p>
                      <h2 className="mt-1 text-2xl font-bold text-slate-950">Built for operational clarity, not dashboard clutter</h2>
                    </div>
                    <div className="w-fit rounded-2xl bg-slate-950 px-4 py-3 text-white animate-float-soft">
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-300">Design</p>
                      <p className="text-base font-semibold">Clean and responsive</p>
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl bg-emerald-50 p-4">
                      <p className="text-sm font-medium text-emerald-700">Occupancy workflow</p>
                      <p className="mt-2 text-base font-bold text-emerald-800">Track rooms, beds, and tenant movement with more confidence.</p>
                    </div>
                    <div className="rounded-2xl bg-amber-50 p-4">
                      <p className="text-sm font-medium text-amber-700">Collections flow</p>
                      <p className="mt-2 text-base font-bold text-amber-800">Manage dues and rent updates in a clearer monthly rhythm.</p>
                    </div>
                    <div className="rounded-2xl bg-cyan-50 p-4">
                      <p className="text-sm font-medium text-cyan-700">Business reporting</p>
                      <p className="mt-2 text-base font-bold text-cyan-800">Review profitability and expenses without spreadsheet cleanup.</p>
                    </div>
                  </div>

                  <div className="mt-5 rounded-[22px] border border-slate-200 bg-slate-950 p-4 sm:p-5 text-white">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                      <div>
                        <p className="text-sm text-slate-400">What Solucate helps organize</p>
                        <p className="text-lg font-semibold">One system for day-to-day PG operations</p>
                      </div>
                      <div className="rounded-full bg-emerald-400/15 px-3 py-1 text-xs font-semibold text-emerald-300 w-fit">
                        Super Responsive
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex flex-col gap-3 rounded-2xl bg-white/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                          <div className="rounded-xl bg-white/10 p-2"><Building2 className="w-4 h-4 text-emerald-300" /></div>
                          <span className="text-sm text-slate-200">Floor, room, and bed structure</span>
                        </div>
                        <span className="text-sm font-semibold text-white">Visual control</span>
                      </div>
                      <div className="flex flex-col gap-3 rounded-2xl bg-white/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                          <div className="rounded-xl bg-white/10 p-2"><Users2 className="w-4 h-4 text-cyan-300" /></div>
                          <span className="text-sm text-slate-200">Tenant records and movement</span>
                        </div>
                        <span className="text-sm font-semibold text-white">Daily workflows</span>
                      </div>
                      <div className="flex flex-col gap-3 rounded-2xl bg-white/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                          <div className="rounded-xl bg-white/10 p-2"><Wallet2 className="w-4 h-4 text-amber-300" /></div>
                          <span className="text-sm text-slate-200">Expenses and monthly reporting</span>
                        </div>
                        <span className="text-sm font-semibold text-white">Business visibility</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="platform" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl animate-fade-up">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-emerald-700">Platform</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-950">Everything your property team needs in one place</h2>
            <p className="mt-4 text-lg text-slate-600">
              Solucate keeps the product focused on what operators actually need: better control, better visibility, and less manual follow-up.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className={`animate-fade-up rounded-[26px] border border-slate-200 bg-[#fbfbf8] p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_45px_rgba(15,23,42,0.08)] ${index === 1 ? 'animation-delay-150' : index === 2 ? 'animation-delay-300' : index === 3 ? 'animation-delay-450' : ''}`}
                data-testid={`feature-${index}`}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-100 to-cyan-100">
                  <feature.icon className="w-6 h-6 text-slate-900" />
                </div>
                <h3 className="mt-5 text-xl font-bold text-slate-950">{feature.title}</h3>
                <p className="mt-3 text-slate-600 leading-7">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr]">
            <div className="animate-fade-up">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-emerald-300">Why Solucate</p>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold">A more professional feel for everyday PG management.</h2>
              <p className="mt-4 text-lg leading-8 text-slate-300">
                Solucate is meant to feel reliable, polished, and calm across every screen size, while still staying simple enough for quick daily use.
              </p>
            </div>

            <div className="grid gap-5 sm:grid-cols-3">
              {whyChooseUs.map((item, index) => (
                <div
                  key={item.title}
                  className={`animate-fade-up rounded-[28px] border border-white/10 bg-white/5 p-6 ${index === 1 ? 'animation-delay-150' : index === 2 ? 'animation-delay-300' : ''}`}
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10">
                    <item.icon className="w-6 h-6 text-emerald-300" />
                  </div>
                  <h3 className="mt-5 text-xl font-bold">{item.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-300">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="py-20 bg-[#eef3ef]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center animate-fade-up">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-emerald-700">Pricing</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-950">Simple, transparent pricing</h2>
            <p className="mt-4 text-lg text-slate-600">Same pricing structure, redesigned in a cleaner SaaS presentation.</p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3 max-w-6xl mx-auto">
            {pricing.map((plan, index) => (
              <div
                key={plan.name}
                className={`relative animate-fade-up rounded-[28px] border p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 ${
                  plan.popular
                    ? 'border-slate-950 bg-slate-950 text-white shadow-[0_25px_60px_rgba(15,23,42,0.18)]'
                    : 'border-slate-200 bg-white text-slate-900'
                } ${index === 1 ? 'animation-delay-150' : index === 2 ? 'animation-delay-300' : ''}`}
                data-testid={`pricing-${plan.name.toLowerCase()}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-6 rounded-full bg-emerald-300 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-950">
                    Most Popular
                  </div>
                )}

                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-2xl font-bold">{plan.name}</h3>
                    <p className={`mt-2 text-sm ${plan.popular ? 'text-slate-300' : 'text-slate-500'}`}>
                      Ideal for operators who want reliable day-to-day control.
                    </p>
                  </div>
                  <div className={`rounded-2xl px-3 py-2 ${plan.popular ? 'bg-white/10' : 'bg-slate-100'}`}>
                    <Star className={`w-5 h-5 ${plan.popular ? 'text-emerald-300' : 'text-slate-600'}`} />
                  </div>
                </div>

                <div className="mt-8">
                  <span className="text-5xl font-bold">₹{plan.price}</span>
                  <span className={`ml-2 text-sm ${plan.popular ? 'text-slate-300' : 'text-slate-500'}`}>/bed/month</span>
                </div>

                <div className="mt-8 space-y-3">
                  {plan.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle2 className={`mt-0.5 w-5 h-5 flex-shrink-0 ${plan.popular ? 'text-emerald-300' : 'text-emerald-600'}`} />
                      <span className={plan.popular ? 'text-slate-200' : 'text-slate-700'}>{feature}</span>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={() => openRegister(plan.name)}
                  variant={plan.popular ? 'secondary' : 'outline'}
                  className={`mt-8 h-12 w-full rounded-full font-semibold ${
                    plan.popular
                      ? 'bg-white text-slate-950 hover:bg-slate-100'
                      : 'border-slate-300 bg-white text-slate-900 hover:bg-slate-50'
                  }`}
                  data-testid={`get-started-${plan.name.toLowerCase()}`}
                >
                  Get Started
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="faq" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center animate-fade-up">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-emerald-700">FAQ</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-950">Questions operators usually ask first</h2>
          </div>

          <div className="mt-10 space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={faq.question}
                className="overflow-hidden rounded-[24px] border border-slate-200 bg-slate-50"
                data-testid={`faq-${index}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-5 sm:px-6 text-left"
                >
                  <span className="text-base font-semibold text-slate-900">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                <div className={`grid transition-all duration-300 ${openFaq === index ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                  <div className="overflow-hidden">
                    <p className="px-5 pb-5 sm:px-6 text-slate-600 leading-7">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-[34px] border border-slate-200 bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 px-6 py-12 text-center text-white shadow-[0_30px_80px_rgba(15,23,42,0.18)] sm:px-10">
            <div className="absolute left-8 top-8 h-24 w-24 rounded-full bg-emerald-400/10 blur-2xl" />
            <div className="absolute right-8 bottom-8 h-24 w-24 rounded-full bg-cyan-300/10 blur-2xl" />
            <p className="relative text-sm font-semibold uppercase tracking-[0.22em] text-emerald-300">Get Started</p>
            <h2 className="relative mt-4 text-3xl sm:text-4xl font-bold">Bring your PG operations under one roof with Solucate.</h2>
            <p className="relative mt-4 text-lg text-slate-300 max-w-2xl mx-auto">
              Move from scattered records to a more polished, responsive system for occupancy, collections, and reporting.
            </p>
            <div className="relative mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                onClick={() => openRegister('Gold')}
                className="h-14 w-full sm:w-auto rounded-full bg-white px-7 text-base font-semibold text-slate-950 hover:bg-slate-100"
                data-testid="final-cta-register"
              >
                Start with Solucate
              </Button>
              <Button
                onClick={() => document.getElementById('platform').scrollIntoView({ behavior: 'smooth' })}
                variant="outline"
                className="h-14 w-full sm:w-auto rounded-full border-white/25 bg-transparent px-7 text-base font-semibold text-white hover:bg-white/10"
              >
                Explore platform
              </Button>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-950">
                <Sparkles className="w-5 h-5 text-emerald-300" />
              </div>
              <div>
                <p className="font-bold text-slate-950">Solucate</p>
                <p className="text-sm text-slate-500">Professional software for PG operations</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-5 text-sm text-slate-500">
              <a href="#platform" className="hover:text-slate-900">Platform</a>
              <a href="#pricing" className="hover:text-slate-900">Pricing</a>
              <a href="#faq" className="hover:text-slate-900">FAQ</a>
            </div>
          </div>
          <div className="mt-8 border-t border-slate-200 pt-6 text-sm text-slate-500">
            © 2026 Solucate. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
